<template>
  <el-drawer
    :model-value="visible"
    class="feedback-drawer"
    size="480px"
    direction="rtl"
    append-to-body
    @update:model-value="onVisibleChange"
  >
    <template #header>
      <div class="drawer-header">
        <span class="drawer-title">{{ t('feedback.drawer.title') }}</span>
        <div class="drawer-header__actions">
          <el-button link @click="goHelpCenter">{{ t('feedback.entry.helpCenter') }}</el-button>
          <el-button v-if="view !== 'list'" link type="primary" @click="drawer.showList()">
            {{ t('feedback.drawer.myList') }}
          </el-button>
          <el-button v-else link type="primary" @click="resetAndShowForm">
            {{ t('feedback.entry.submit') }}
          </el-button>
        </div>
      </div>
    </template>

    <div v-if="view === 'form'" class="panel form-panel">
      <p class="question">{{ t('feedback.drawer.question') }}</p>
      <div class="type-grid">
        <button
          v-for="item in typeOptions"
          :key="item"
          type="button"
          class="type-chip"
          :class="{ active: form.type === item }"
          @click="form.type = item"
        >
          {{ t(`feedback.type.${item}`) }}
        </button>
      </div>

      <div class="field">
        <label>{{ t('feedback.drawer.titleLabel') }}</label>
        <el-input v-model="form.title" maxlength="200" show-word-limit :placeholder="t('feedback.drawer.titlePlaceholder')" />
      </div>
      <div class="field field--desc">
        <label>{{ t('feedback.drawer.descLabel') }}</label>
        <el-input
          v-model="form.description"
          type="textarea"
          resize="none"
          :rows="6"
          maxlength="5000"
          show-word-limit
          :placeholder="t('feedback.drawer.descPlaceholder')"
        />
      </div>
      <div class="field field--upload">
        <label>{{ t('feedback.drawer.upload') }}</label>
        <el-upload
          class="feedback-uploader"
          drag
          multiple
          :show-file-list="false"
          :disabled="uploading"
          :http-request="handleUpload"
          :before-upload="beforeUpload"
        >
          <div class="upload-drop" :class="{ busy: uploading }">
            <p class="upload-drop__title">{{ t('feedback.drawer.uploadHint') }}</p>
            <p class="upload-drop__sub">{{ t('feedback.drawer.uploadHintSub') }}</p>
          </div>
        </el-upload>
        <div v-if="pendingFiles.length" class="attach-grid">
          <div v-for="file in pendingFiles" :key="String(file.id)" class="attach-card">
            <a
              v-if="isFeedbackImageType(file.fileType)"
              class="attach-media"
              :href="resolveResourceUrl(file.fileUrl)"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img :src="resolveResourceUrl(file.fileUrl)" :alt="file.fileName" />
            </a>
            <a
              v-else-if="isFeedbackVideoType(file.fileType)"
              class="attach-media"
              :href="resolveResourceUrl(file.fileUrl)"
              target="_blank"
              rel="noopener noreferrer"
            >
              <video :src="resolveResourceUrl(file.fileUrl)" muted preload="metadata" />
            </a>
            <a v-else class="attach-file" :href="resolveResourceUrl(file.fileUrl)" target="_blank" rel="noopener noreferrer">
              {{ file.fileName }}
            </a>
            <button type="button" class="attach-remove" @click="removePendingFile(file.id)">×</button>
          </div>
        </div>
      </div>
      <div class="footer">
        <el-button type="primary" :loading="submitting || uploading" :disabled="uploading" @click="submitForm">
          {{ submitting ? t('feedback.drawer.submitting') : t('feedback.drawer.submit') }}
        </el-button>
      </div>
    </div>

    <div v-else-if="view === 'success'" class="panel success-panel">
      <div class="success-mark" aria-hidden="true">
        <svg viewBox="0 0 52 52">
          <circle class="success-mark__ring" cx="26" cy="26" r="24" />
          <path class="success-mark__check" d="M16 26.5l7.2 7.2L36.5 19.5" />
        </svg>
      </div>
      <h3 class="success-title">{{ t('feedback.drawer.submitted') }}</h3>
      <p class="success-hint">{{ t('feedback.drawer.submittedHint') }}</p>

      <div class="success-ticket">
        <div class="success-ticket__label">{{ t('feedback.drawer.ticketNoLabel') }}</div>
        <div class="success-ticket__row">
          <strong class="success-ticket__no">#{{ lastTicketNo }}</strong>
          <el-button link type="primary" @click="copyTicketNo">
            {{ t('feedback.drawer.copyTicket') }}
          </el-button>
        </div>
        <div class="success-ticket__status">
          <span>{{ t('feedback.drawer.submittedStatusHint') }}</span>
          <el-tag size="small" type="info">{{ t('feedback.status.PENDING') }}</el-tag>
        </div>
      </div>

      <ul class="success-next">
        <li>{{ t('feedback.drawer.nextStep1') }}</li>
        <li>{{ t('feedback.drawer.nextStep2') }}</li>
      </ul>

      <div class="success-actions">
        <el-button type="primary" @click="drawer.showThread(String(lastTicketId))">
          {{ t('feedback.drawer.view') }}
        </el-button>
        <el-button @click="resetAndShowForm">{{ t('feedback.drawer.backForm') }}</el-button>
      </div>
    </div>

    <div v-else-if="view === 'list'" class="panel list-panel" v-loading="listLoading">
      <el-empty v-if="!listLoading && tickets.length === 0" :description="t('feedback.drawer.empty')">
        <el-button type="primary" @click="resetAndShowForm">{{ t('feedback.entry.submit') }}</el-button>
      </el-empty>
      <button
        v-for="item in tickets"
        :key="String(item.id)"
        type="button"
        class="ticket-card"
        @click="drawer.showThread(String(item.id))"
      >
        <div class="ticket-top">
          <strong>#{{ item.ticketNo }}</strong>
          <el-badge v-if="item.userUnreadCount" :value="item.userUnreadCount" />
        </div>
        <div class="ticket-title">{{ item.title }}</div>
        <div class="ticket-meta">
          <el-tag size="small" :type="statusTag(resolveUserFacingStatus(item.status))">
            {{ t(`feedback.status.${resolveUserFacingStatus(item.status)}`) }}
          </el-tag>
          <span>{{ formatFeedbackLocalDateTime(item.createdAt || item.updatedAt) }}</span>
        </div>
      </button>
    </div>

    <div v-else class="chat-panel" v-loading="detailLoading">
      <template v-if="detail">
        <header class="chat-header">
          <div class="chat-header__main">
            <div class="chat-ticket">#{{ detail.ticketNo }}</div>
            <h3 class="chat-title">{{ detail.title }}</h3>
          </div>
          <el-tag size="small" :type="statusTag(resolveUserFacingStatus(detail.status))">
            {{ t(`feedback.status.${resolveUserFacingStatus(detail.status)}`) }}
          </el-tag>
        </header>

        <div ref="messageListRef" class="chat-stream">
          <div
            v-for="msg in detail.messages || []"
            :key="String(msg.id)"
            class="chat-row"
            :class="rowClass(msg.senderType)"
          >
            <div class="chat-avatar" :class="rowClass(msg.senderType)">
              {{ avatarText(msg.senderType) }}
            </div>
            <div class="chat-col">
              <div class="chat-meta">
                <span>{{ senderLabel(msg.senderType) }}</span>
                <span>{{ formatFeedbackLocalDateTime(msg.createdAt) }}</span>
              </div>
              <div class="chat-bubble" :class="rowClass(msg.senderType)">
                <div v-if="msg.content" class="chat-text">{{ msg.content }}</div>
                <div v-if="msg.attachments?.length" class="attach-grid">
                  <div v-for="file in msg.attachments" :key="String(file.id)" class="attach-card readonly">
                    <el-image
                      v-if="isFeedbackImageType(file.fileType)"
                      class="attach-media"
                      :src="resolveResourceUrl(file.fileUrl)"
                      :alt="file.fileName"
                      fit="cover"
                      :preview-src-list="feedbackImagePreviewList(msg.attachments).map(resolveResourceUrl)"
                      :initial-index="feedbackImagePreviewIndex(msg.attachments, file.fileUrl)"
                      preview-teleported
                    />
                    <video
                      v-else-if="isFeedbackVideoType(file.fileType)"
                      class="attach-media"
                      :src="resolveResourceUrl(file.fileUrl)"
                      muted
                      preload="metadata"
                      controls
                    />
                    <a v-else class="attach-file" :href="resolveResourceUrl(file.fileUrl)" target="_blank" rel="noopener noreferrer">
                      {{ file.fileName }}
                    </a>
                  </div>
                </div>
              </div>
              <div v-if="isMessageReadByPeer(msg)" class="chat-read">
                {{ t('feedback.chat.read') }}
              </div>
            </div>
          </div>
        </div>

        <footer v-if="canReply" class="chat-composer">
          <div v-if="replyFiles.length" class="composer-files">
            <div v-for="file in replyFiles" :key="file.uid" class="composer-file">
              <el-image
                v-if="isFeedbackImageType(file.fileType)"
                class="composer-thumb"
                :src="resolveResourceUrl(file.fileUrl)"
                fit="cover"
                :preview-src-list="[resolveResourceUrl(file.fileUrl)]"
                preview-teleported
              />
              <video
                v-else-if="isFeedbackVideoType(file.fileType)"
                class="composer-thumb"
                :src="resolveResourceUrl(file.fileUrl)"
                muted
                preload="metadata"
              />
              <span v-else class="composer-file-name">{{ file.fileName }}</span>
              <button type="button" class="composer-remove" @click="removeReplyFile(file.uid)">×</button>
            </div>
          </div>
          <el-input
            v-model="replyContent"
            type="textarea"
            :rows="3"
            resize="none"
            :placeholder="t('feedback.drawer.replyPlaceholder')"
            @keydown.enter.exact.prevent="handleReply"
          />
          <div class="chat-composer__bar">
            <div class="chat-composer__left">
              <el-upload
                :show-file-list="false"
                :disabled="uploading"
                multiple
                :http-request="handleReplyUpload"
                :before-upload="beforeUpload"
              >
                <el-button :disabled="uploading">{{ t('feedback.manage.attach') }}</el-button>
              </el-upload>
              <el-button v-if="detail.status !== 'CLOSED'" @click="handleClose">{{ t('feedback.drawer.close') }}</el-button>
            </div>
            <el-button
              type="primary"
              :loading="replying || uploading"
              :disabled="(!replyContent.trim() && !replyFiles.length) || uploading"
              @click="handleReply"
            >
              {{ t('feedback.drawer.send') }}
            </el-button>
          </div>
        </footer>
        <footer v-else class="chat-composer chat-composer--actions">
          <el-button v-if="detail.status !== 'CLOSED'" @click="handleClose">{{ t('feedback.drawer.close') }}</el-button>
          <el-button type="primary" @click="handleReopen">{{ t('feedback.drawer.reopen') }}</el-button>
        </footer>
      </template>
    </div>
  </el-drawer>

  <button
    v-if="showFab"
    type="button"
    class="feedback-fab"
    :title="t('feedback.entry.label')"
    @click="openFeedbackEntry"
  >
    <el-icon :size="16"><ChatDotRound /></el-icon>
    <span>{{ t('feedback.entry.label') }}</span>
    <span v-if="unreadCount" class="feedback-fab__badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage, type UploadRequestOptions } from 'element-plus'
import { ChatDotRound } from '@element-plus/icons-vue'
import { useUserStore } from '@/pinia/stores/user'
import { uploadFileSmart } from '@/common/apis/upload'
import {
  closeMyFeedbackTicket,
  createFeedbackTicket,
  getMyFeedbackTicket,
  listMyFeedbackTickets,
  registerFeedbackAttachment,
  reopenMyFeedbackTicket,
  replyMyFeedbackTicket,
  resolveUserFacingStatus,
  type FeedbackTicketDetail,
  type FeedbackTicketListItem,
  type FeedbackType
} from '@/common/apis/feedback'
import { collectFeedbackClientContext } from '@/common/composables/useFeedbackContext'
import { onFeedbackRealtime } from '@/common/composables/useFeedbackRealtime'
import { isFeedbackMessageReadByPeer } from '@/common/utils/feedback-read'
import { useFeedbackDrawer } from '@/common/composables/useFeedbackDrawer'
import { openFeedbackEntry, useFeedbackUnread } from '@/common/composables/useFeedbackUnread'
import { toApiId } from '@/common/utils/id'
import {
  feedbackImagePreviewIndex,
  feedbackImagePreviewList,
  isFeedbackImageType,
  isFeedbackVideoType
} from '@/common/utils/feedback-attachment'
import { formatFeedbackLocalDateTime } from '@/common/utils/feedback-datetime'
import { resolveResourceUrl } from '@/common/config'
import { copyTextToClipboard } from '@/common/utils/clipboard'

const IMAGE_MAX = 10 * 1024 * 1024
const VIDEO_MAX = 200 * 1024 * 1024
const FILE_MAX = 50 * 1024 * 1024
const typeOptions: FeedbackType[] = ['BUG', 'SUGGESTION', 'DATA_ERROR', 'OTHER']

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const drawer = useFeedbackDrawer()
const { visible, view, activeTicketId, lastTicketNo, lastTicketId } = drawer
const { unreadCount, refresh: refreshUnread, applyFromList } = useFeedbackUnread()

const showFab = computed(() => Boolean(userStore.token) && route.path.includes('task-hall'))
const canReply = computed(() => {
  const status = detail.value?.status
  return status !== 'RESOLVED' && status !== 'CLOSED'
})

interface PendingAttachment {
  id: string | number
  fileName: string
  fileUrl: string
  fileType: string
}

const form = reactive({
  type: '' as FeedbackType | '',
  title: '',
  description: ''
})
const pendingFiles = ref<PendingAttachment[]>([])
const submitting = ref(false)
const uploadingCount = ref(0)
const uploading = computed(() => uploadingCount.value > 0)
const tickets = ref<FeedbackTicketListItem[]>([])
const listLoading = ref(false)
const detail = ref<FeedbackTicketDetail | null>(null)
const detailLoading = ref(false)
const replyContent = ref('')
const replyFiles = ref<Array<PendingAttachment & { uid: string }>>([])
const replying = ref(false)
const messageListRef = ref<HTMLElement | null>(null)

function rowClass(senderType: string) {
  if (senderType === 'STAFF') return 'staff'
  if (senderType === 'SYSTEM') return 'system'
  return 'user'
}

/** 用户只看自己发出的消息是否已被客服读 */
function isMessageReadByPeer(msg: Parameters<typeof isFeedbackMessageReadByPeer>[0]) {
  return isFeedbackMessageReadByPeer(msg, 'USER', detail.value?.staffLastReadMessageId)
}

function avatarText(senderType: string) {
  if (senderType === 'STAFF') return t('feedback.sender.STAFF').slice(0, 1)
  if (senderType === 'USER') return t('feedback.sender.USER').slice(0, 1)
  return '系'
}

async function scrollToBottom() {
  await nextTick()
  const el = messageListRef.value
  if (el) el.scrollTop = el.scrollHeight
}

function collectClipboardFiles(event: ClipboardEvent): File[] {
  const files: File[] = []
  const items = event.clipboardData?.items
  if (!items?.length) return files
  for (const item of Array.from(items)) {
    if (item.kind !== 'file') continue
    const file = item.getAsFile()
    if (!file) continue
    if (!file.name || file.name === 'image.png') {
      const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg')
      files.push(new File([file], `paste-${Date.now()}.${ext}`, { type: file.type || 'image/png' }))
    } else {
      files.push(file)
    }
  }
  return files
}

function canAcceptPaste() {
  if (!visible.value) return false
  if (view.value === 'form') return true
  return view.value === 'thread' && canReply.value
}

async function onPaste(event: ClipboardEvent) {
  if (!canAcceptPaste()) return
  const files = collectClipboardFiles(event)
  if (!files.length) return
  event.preventDefault()
  const target = view.value === 'thread' ? 'reply' : 'form'
  await ingestFiles(files, target)
}

watch(
  visible,
  (isVisible) => {
    if (isVisible) window.addEventListener('paste', onPaste)
    else window.removeEventListener('paste', onPaste)
  },
  { immediate: true }
)

const stopRealtime = onFeedbackRealtime((payload) => {
  if (!visible.value) return
  // 本端刚回复过会自行 loadDetail；忽略自己发出的 USER 回复推送，避免重复请求
  if (payload.event === 'FEEDBACK_REPLY' && payload.senderType === 'USER') return

  if (view.value === 'list') {
    void loadList()
    return
  }
  if (view.value !== 'thread' || !activeTicketId.value) return

  const tid = payload.ticketId != null ? String(payload.ticketId) : ''
  if (tid && tid !== String(activeTicketId.value)) {
    // 其它工单有更新：只静默刷新列表缓存，不打断当前对话
    void loadList()
    return
  }
  void loadDetail({ preserveComposer: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('paste', onPaste)
  stopRealtime()
})

watch(
  [visible, view, activeTicketId],
  ([isVisible, currentView, ticketId]) => {
    if (!isVisible) return
    if (currentView === 'list') loadList()
    if (currentView === 'thread' && ticketId) loadDetail()
  }
)

function onVisibleChange(val: boolean) {
  if (!val) drawer.close()
}

function resetAndShowForm() {
  form.type = ''
  form.title = ''
  form.description = ''
  pendingFiles.value = []
  drawer.showForm()
}

function goHelpCenter() {
  drawer.close()
  router.push('/help')
}

async function copyTicketNo() {
  const ticketNo = lastTicketNo.value
  if (!ticketNo) return
  try {
    await copyTextToClipboard(`#${ticketNo}`)
    ElMessage.success(t('feedback.drawer.copyTicketSuccess'))
  } catch {
    ElMessage.error(t('feedback.drawer.loadFailed'))
  }
}

function statusTag(status: string) {
  const map: Record<string, 'info' | 'warning' | 'success' | 'primary'> = {
    PENDING: 'info',
    PROCESSING: 'warning',
    WAITING_USER: 'primary',
    RESOLVED: 'success',
    CLOSED: 'info'
  }
  return map[status] || 'info'
}

function senderLabel(type: string) {
  if (type === 'USER') return t('feedback.sender.USER')
  if (type === 'STAFF') return t('feedback.sender.STAFF')
  return t('feedback.sender.SYSTEM')
}

function beforeUpload(file: File) {
  const max = file.type.startsWith('image/') ? IMAGE_MAX : file.type.startsWith('video/') ? VIDEO_MAX : FILE_MAX
  if (file.size > max) {
    ElMessage.warning(t('feedback.drawer.uploadSizeExceeded'))
    return false
  }
  return true
}

function extractUploadInfo(response: unknown) {
  const payload = response as {
    data?: {
      data?: { results?: Array<{ file_info?: Record<string, unknown> }> }
      results?: Array<{ file_info?: Record<string, unknown> }>
    }
  }
  const body = payload.data
  const info = (body?.data?.results ?? body?.results)?.[0]?.file_info || {}
  return {
    fileUrl: String(info.file_url || ''),
    storageKey: String(info.file_path || ''),
    fileName: String(info.original_filename || ''),
    fileSize: Number(info.file_size || 0),
    fileType: String(info.content_type || '')
  }
}

async function uploadAndRegister(file: File): Promise<PendingAttachment> {
  const uploadType = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'common'
  const uploaded = await uploadFileSmart(file, uploadType)
  const info = extractUploadInfo(uploaded)
  if (!info.fileUrl) throw new Error('upload failed')
  const fileName = info.fileName || file.name
  const fileType = info.fileType || file.type
  const registered = await registerFeedbackAttachment({
    fileUrl: info.fileUrl,
    storageKey: info.storageKey,
    fileName,
    fileSize: info.fileSize || file.size,
    fileType
  })
  const id = registered.data?.data?.attachmentId
  if (id == null) throw new Error('register failed')
  return { id, fileName, fileUrl: info.fileUrl, fileType }
}

async function ingestFiles(files: File[], target: 'form' | 'reply'): Promise<boolean> {
  let allOk = true
  for (const file of files) {
    // el-upload 点击/拖拽已走过 beforeUpload；粘贴路径需要这里再校验
    if (!beforeUpload(file)) {
      allOk = false
      continue
    }
    uploadingCount.value += 1
    try {
      const result = await uploadAndRegister(file)
      if (target === 'form') {
        pendingFiles.value.push(result)
      } else {
        replyFiles.value.push({ uid: `${Date.now()}-${result.id}`, ...result })
      }
    } catch {
      allOk = false
      ElMessage.error(t('feedback.drawer.loadFailed'))
    } finally {
      uploadingCount.value -= 1
    }
  }
  return allOk
}

async function handleUpload(options: UploadRequestOptions) {
  const ok = await ingestFiles([options.file as File], 'form')
  if (ok) options.onSuccess?.({})
  else options.onError?.(new Error('upload failed') as any)
}

function removePendingFile(id: string | number) {
  pendingFiles.value = pendingFiles.value.filter((item) => String(item.id) !== String(id))
}

async function handleReplyUpload(options: UploadRequestOptions) {
  const ok = await ingestFiles([options.file as File], 'reply')
  if (ok) options.onSuccess?.({})
  else options.onError?.(new Error('upload failed') as any)
}

function removeReplyFile(uid: string) {
  replyFiles.value = replyFiles.value.filter((item) => item.uid !== uid)
}

async function submitForm() {
  if (uploading.value) {
    ElMessage.warning(t('feedback.drawer.waitUpload'))
    return
  }
  if (!form.type) {
    ElMessage.warning(t('feedback.drawer.typeRequired'))
    return
  }
  if (!form.title.trim()) {
    ElMessage.warning(t('feedback.drawer.titleRequired'))
    return
  }
  if (!form.description.trim()) {
    ElMessage.warning(t('feedback.drawer.descRequired'))
    return
  }
  submitting.value = true
  try {
    const ctx = collectFeedbackClientContext(route)
    const res = await createFeedbackTicket({
      type: form.type,
      title: form.title.trim(),
      description: form.description.trim(),
      attachmentIds: pendingFiles.value.map((item) => Number(item.id)).filter((id) => id > 0),
      ...ctx
    })
    const data = res.data?.data
    if (!data?.ticketNo) throw new Error('create failed')
    pendingFiles.value = []
    form.type = ''
    form.title = ''
    form.description = ''
    drawer.setSuccess(toApiId(data.id), data.ticketNo)
  } catch {
    ElMessage.error(t('feedback.drawer.loadFailed'))
  } finally {
    submitting.value = false
  }
}

async function loadList() {
  listLoading.value = true
  try {
    const res = await listMyFeedbackTickets({ pageNo: 1, pageSize: 50 })
    tickets.value = res.data?.data?.list || []
    applyFromList(tickets.value)
  } catch {
    ElMessage.error(t('feedback.drawer.loadFailed'))
  } finally {
    listLoading.value = false
  }
}

async function loadDetail(options?: { preserveComposer?: boolean }) {
  if (!activeTicketId.value) return
  detailLoading.value = true
  try {
    const res = await getMyFeedbackTicket(activeTicketId.value)
    detail.value = res.data?.data || null
    if (!options?.preserveComposer) {
      replyContent.value = ''
      replyFiles.value = []
    }
    // 打开对话即已读：清列表未读角标，并以接口为准刷新顶栏角标
    const openedId = String(activeTicketId.value)
    tickets.value = tickets.value.map((item) =>
      String(item.id) === openedId ? { ...item, userUnreadCount: 0 } : item
    )
    void refreshUnread()
    await scrollToBottom()
  } catch {
    ElMessage.error(t('feedback.drawer.loadFailed'))
  } finally {
    detailLoading.value = false
  }
}

async function handleReply() {
  if (!detail.value || replying.value) return
  if (uploading.value) {
    ElMessage.warning(t('feedback.drawer.waitUpload'))
    return
  }
  const content = replyContent.value.trim()
  if (!content && !replyFiles.value.length) return
  replying.value = true
  try {
    await replyMyFeedbackTicket(
      detail.value.id,
      content,
      replyFiles.value.map((item) => item.id)
    )
    replyContent.value = ''
    replyFiles.value = []
    await loadDetail()
  } catch {
    ElMessage.error(t('feedback.drawer.loadFailed'))
  } finally {
    replying.value = false
  }
}

async function handleClose() {
  if (!detail.value) return
  await closeMyFeedbackTicket(detail.value.id)
  await loadDetail()
}

async function handleReopen() {
  if (!detail.value) return
  await reopenMyFeedbackTicket(detail.value.id)
  await loadDetail()
}
</script>

<style scoped lang="scss">
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 28px;
  gap: 12px;
}
.drawer-header__actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.drawer-title {
  font-size: 16px;
  font-weight: 600;
}
.panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
}
.form-panel {
  overflow: hidden;
  gap: 14px;
}
.question {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--el-text-color-regular);
}
.type-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  flex-shrink: 0;
}
.type-chip {
  height: 40px;
  border: 1px solid var(--el-border-color);
  background: var(--el-bg-color);
  border-radius: 6px;
  color: var(--el-text-color-primary);
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: var(--el-color-primary-light-5);
    color: var(--el-color-primary);
  }
  &.active {
    border-color: var(--el-color-primary);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
}
.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
  label {
    font-size: 13px;
    color: var(--el-text-color-regular);
  }
  &--desc {
    flex: 1;
    min-height: 132px;
    :deep(.el-textarea) {
      flex: 1;
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    :deep(.el-textarea__inner) {
      flex: 1;
      height: 100% !important;
      min-height: 0;
      resize: none;
    }
  }
  &--upload {
    :deep(.el-upload-dragger) {
      height: auto;
    }
  }
}
.feedback-uploader {
  width: 100%;
  :deep(.el-upload),
  :deep(.el-upload-dragger) {
    width: 100%;
  }
  :deep(.el-upload-dragger) {
    padding: 0;
    border-radius: 8px;
    background: var(--el-fill-color-blank);
  }
  &.reply {
    flex: 1;
    min-width: 0;
    :deep(.el-upload-dragger) {
      height: auto;
    }
  }
}
.upload-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 88px;
  padding: 18px 12px;
  &--compact {
    min-height: 64px;
    padding: 10px 12px;
  }
  &.busy {
    opacity: 0.65;
  }
  &__title {
    margin: 0;
    font-size: 14px;
    color: var(--el-text-color-primary);
  }
  &__sub {
    margin: 0;
    font-size: 12px;
    color: var(--el-text-color-secondary);
  }
}
.attach-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 6px;
  margin-top: 8px;
}
.attach-card {
  position: relative;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  overflow: hidden;
  background: var(--el-fill-color-blank);
  min-height: 72px;
}
.attach-card.readonly {
  min-height: 64px;
}
.attach-media {
  display: block;
  width: 100%;
  height: 72px;
  cursor: zoom-in;
  :deep(img),
  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    background: #000;
  }
}
.attach-file {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 64px;
  height: auto;
  padding: 8px;
  font-size: 12px;
  color: var(--el-color-primary);
  text-align: center;
  word-break: break-all;
  text-decoration: none;
}
.attach-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  line-height: 18px;
  cursor: pointer;
}
.footer {
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  padding-top: 4px;
}
.success-panel {
  flex: 1;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0;
  padding: 32px 4px 40px;
  overflow: auto;
}
.success-mark {
  width: 72px;
  height: 72px;
  margin-bottom: 18px;
  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
  &__ring,
  &__check {
    fill: none;
    stroke: var(--el-color-success);
    stroke-width: 2.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  &__ring {
    stroke-dasharray: 151;
    stroke-dashoffset: 151;
    animation: success-ring 0.55s ease-out forwards;
  }
  &__check {
    stroke-width: 3;
    stroke-dasharray: 36;
    stroke-dashoffset: 36;
    animation: success-check 0.35s 0.4s ease-out forwards;
  }
}
@keyframes success-ring {
  to { stroke-dashoffset: 0; }
}
@keyframes success-check {
  to { stroke-dashoffset: 0; }
}
.success-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: var(--el-text-color-primary);
}
.success-hint {
  margin: 0 0 20px;
  max-width: 320px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--el-text-color-secondary);
}
.success-ticket {
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 12px;
  background: var(--el-fill-color-blank);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}
.success-ticket__label {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.success-ticket__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 6px 0 10px;
}
.success-ticket__no {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.4px;
  word-break: break-all;
}
.success-ticket__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.success-next {
  width: 100%;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  li {
    position: relative;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.5;
    color: var(--el-text-color-regular);
    &::before {
      content: '';
      position: absolute;
      left: 2px;
      top: 7px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--el-color-primary);
    }
  }
}
.success-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
  :deep(.el-button) {
    width: 100%;
    margin: 0;
  }
}
.list-panel {
  gap: 10px;
  overflow: auto;
}
.ticket-card {
  text-align: left;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 12px;
  padding: 12px 14px;
  background: var(--el-bg-color);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    border-color: var(--el-color-primary-light-5);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
}
.ticket-top, .ticket-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ticket-title {
  margin: 6px 0;
  font-weight: 500;
}
.ticket-meta {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.chat-panel {
  flex: 1;
  height: auto;
  max-height: none;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin: -12px -20px -20px;
  background: var(--el-bg-color-page);
  color: var(--el-text-color-primary);
}
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--el-bg-color);
  border-bottom: 1px solid var(--el-border-color-lighter);
  flex-shrink: 0;
}
.chat-header__main {
  min-width: 0;
}
.chat-ticket {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}
.chat-title {
  margin: 4px 0 0;
  font-size: 16px;
  line-height: 1.35;
  word-break: break-word;
}
.chat-stream {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 16px 14px 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.chat-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 92%;
  &.user {
    align-self: flex-end;
    flex-direction: row-reverse;
  }
  &.staff {
    align-self: flex-start;
  }
  &.system {
    align-self: center;
    max-width: 100%;
  }
}
.chat-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  color: #fff;
  &.user { background: var(--el-color-primary); }
  &.staff { background: #909399; }
  &.system { background: #c0c4cc; }
}
.chat-col {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.chat-row.user .chat-col {
  align-items: flex-end;
}
.chat-row.system .chat-col {
  align-items: center;
}
.chat-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: var(--el-text-color-secondary);
}
.chat-row.user .chat-meta {
  flex-direction: row-reverse;
}
.chat-bubble {
  max-width: 300px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--el-bg-color);
  color: var(--el-text-color-primary);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  &.user {
    background: var(--el-color-primary-light-8);
    border-top-right-radius: 4px;
  }
  &.staff {
    border-top-left-radius: 4px;
  }
  &.system {
    background: transparent;
    box-shadow: none;
    color: var(--el-text-color-secondary);
    font-size: 12px;
    text-align: center;
  }
}
.chat-read {
  font-size: 11px;
  color: var(--el-text-color-secondary);
  line-height: 1;
  padding: 0 2px;
}
.chat-text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  font-size: 14px;
  color: var(--el-text-color-primary);
}
.chat-composer {
  flex-shrink: 0;
  padding: 12px 14px 14px;
  background: var(--el-bg-color);
  border-top: 1px solid var(--el-border-color-lighter);
  display: flex;
  flex-direction: column;
  gap: 8px;
  &--actions {
    flex-direction: row;
    justify-content: flex-end;
  }
}
.chat-composer__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.chat-composer__left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.composer-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.composer-file {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--el-border-color-lighter);
  background: var(--el-fill-color-blank);
}
.composer-thumb {
  width: 100%;
  height: 100%;
  display: block;
  :deep(img),
  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    background: #000;
  }
}
.composer-file-name {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 4px;
  font-size: 11px;
  text-align: center;
  word-break: break-all;
  color: var(--el-color-primary);
}
.composer-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  line-height: 16px;
  cursor: pointer;
}
.feedback-fab {
  position: fixed;
  right: 20px;
  bottom: 24px;
  z-index: 2000;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 20px;
  background: var(--el-color-primary);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
}
.feedback-fab__badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 2px;
  border-radius: 9px;
  background: #fff;
  color: var(--el-color-primary);
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}
</style>

<style lang="scss">
.feedback-drawer.el-drawer {
  .el-drawer__header {
    margin-bottom: 0;
    padding: 16px 20px 12px;
  }
  .el-drawer__body {
    display: flex;
    flex-direction: column;
    padding: 12px 20px 20px;
    overflow: hidden;
  }
}
</style>
